<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';
import { useToast } from 'primevue/usetoast';
import SendForApprovalModal from '@/components/admin/orders/SendForApprovalModal.vue';
import axios from 'axios';

defineOptions({ inheritAttrs: false });

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();

const props = defineProps({
    order: { type: Object, required: true },
    itemsWithContent: { type: Array, default: () => [] },
    isEnabled: { type: Boolean, default: false },
    stepNumber: { type: Number, default: 3 },
});

const emit = defineEmits(['refresh-needed']);

const adminLinkInput = ref({});
const showAdminLinkModal = ref(false);
const selectedContentId = ref(null);
const addingLink = ref(false);
const showClearLinkModal = ref(false);
const selectedClearContentId = ref(null);
const showSendToClientApprovalModal = ref(false);
const selectedSendApprovalContentId = ref(null);
const sendingToClientApproval = ref(false);

const autoContentItems = computed(() =>
    props.itemsWithContent.filter(item => item?.content?.auto_content === 1)
);

const allApproved = computed(() => {
    const items = autoContentItems.value;
    return items.length > 0 && items.every(item => !!item.content.client_approved_at);
});

const openAdminLinkModal = (contentId) => {
    selectedContentId.value = contentId;
    const item = props.itemsWithContent.find(i => i.content?.id === contentId);
    adminLinkInput.value[contentId] = item?.content?.content_link || '';
    showAdminLinkModal.value = true;
};

const closeAdminLinkModal = () => {
    showAdminLinkModal.value = false;
    selectedContentId.value = null;
    adminLinkInput.value = {};
    addingLink.value = false;
};

const isValidGoogleDocsLink = (url) => {
    try {
        const parsed = new URL(url.trim());
        return parsed.hostname === 'docs.google.com' && parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

const saveAdminLink = async () => {
    const contentId = selectedContentId.value;
    const link = adminLinkInput.value[contentId];
    if (!link?.trim() || !isValidGoogleDocsLink(link)) {
        alert($t('Please enter a valid Google Docs link'));
        return;
    }

    // Validate anchor name + URL are present in the article
    const item = autoContentItems.value.find(i => i.content?.id === contentId);
    if (item?.content) {
        try {
            showLoading($t('Validating Google Docs content...'));
            const response = await axios.post('/google-docs/validate-content', {
                google_docs_url: link.trim(),
                anchor_name: item.content.anchor_name?.trim() || null,
                anchor_url: item.content.anchor_url?.trim() || null,
                anchor_name_2: item.content.anchor_name_2?.trim() || null,
                anchor_url_2: item.content.anchor_url_2?.trim() || null,
                anchor_name_3: item.content.anchor_name_3?.trim() || null,
                anchor_url_3: item.content.anchor_url_3?.trim() || null,
            });
            hideLoading();

            if (response.data.valid === false) {
                const errorDetail = Array.isArray(response.data.errors)
                    ? response.data.errors.join('\n')
                    : (response.data.error || $t('Google Docs validation failed'));
                toast.add({ severity: 'error', summary: $t('Validation Error'), detail: errorDetail, life: 8000 });
                return;
            }
        } catch (error) {
            hideLoading();
            if (error.response?.data?.valid === false) {
                const errorDetail = Array.isArray(error.response.data.errors)
                    ? error.response.data.errors.join('\n')
                    : (error.response.data.error || $t('Google Docs validation failed'));
                toast.add({ severity: 'error', summary: $t('Validation Error'), detail: errorDetail, life: 8000 });
                return;
            }
            // If validation endpoint fails, warn but allow saving
            toast.add({ severity: 'warn', summary: $t('Validation Warning'), detail: $t('Could not validate Google Docs content. Proceeding anyway.'), life: 3000 });
        }
    }

    addingLink.value = true;
    router.post(
        proxy.route ? proxy.route('order-content.add-link', contentId) : `/admin/order-content/${contentId}/add-link`,
        { content_link: link.trim() },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Adding Google Docs link...')),
            onSuccess: () => { addingLink.value = false; closeAdminLinkModal(); emit('refresh-needed'); },
            onFinish: () => { hideLoading(); addingLink.value = false; },
            onError: () => { hideLoading(); addingLink.value = false; },
        }
    );
};

const clearContentLink = (contentId) => {
    selectedClearContentId.value = contentId;
    showClearLinkModal.value = true;
};

const confirmClearLink = () => {
    const contentId = selectedClearContentId.value;
    router.post(
        proxy.route ? proxy.route('order-content.clear-link', contentId) : `/admin/order-content/${contentId}/clear-link`,
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Clearing Google Docs link...')),
            onFinish: () => hideLoading(),
            onSuccess: () => { showClearLinkModal.value = false; selectedClearContentId.value = null; emit('refresh-needed'); },
            onError: () => hideLoading(),
        }
    );
};

const openSendToClientApprovalModal = (contentId) => {
    selectedSendApprovalContentId.value = contentId;
    showSendToClientApprovalModal.value = true;
};

const closeSendToClientApprovalModal = () => {
    showSendToClientApprovalModal.value = false;
    selectedSendApprovalContentId.value = null;
    sendingToClientApproval.value = false;
};

const submitSendToClientApproval = (message = null, emailLocale = null) => {
    const contentId = selectedSendApprovalContentId.value;
    router.post(
        proxy.route ? proxy.route('order-content.send-to-client-approval', contentId) : `/admin/order-content/${contentId}/send-to-client-approval`,
        { message, email_locale: emailLocale },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => { sendingToClientApproval.value = true; showLoading($t('Sending to client for approval...')); },
            onFinish: () => { hideLoading(); sendingToClientApproval.value = false; },
            onSuccess: () => { closeSendToClientApprovalModal(); emit('refresh-needed'); },
            onError: (errors) => {
                toast.add({ severity: 'error', summary: $t('Error'), detail: errors.error || $t('Failed to send for client approval'), life: 5000 });
            },
        }
    );
};
</script>

<template>
    <div :class="$attrs.class">
        <Card :class="{ 'opacity-100': isEnabled, 'opacity-60': !isEnabled }">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-bold" :class="isEnabled ? 'text-surface-900' : 'text-surface-400'">
                                {{ $t('Article Approval') }}
                            </h2>
                            <p class="text-sm font-medium" :class="allApproved ? 'text-green-600' : (isEnabled ? 'text-amber-600' : 'text-surface-400')">
                                <span v-if="!isEnabled">{{ $t('Blocked until content review is confirmed') }}</span>
                                <span v-else-if="allApproved">{{ $t('All articles approved by client') }}</span>
                                <span v-else>{{ $t('Pending client article approval') }}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div v-if="!isEnabled" class="text-center py-12">
                    <i class="pi pi-lock text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-500 mb-2">{{ $t('Content Review Required') }}</h3>
                    <p class="text-surface-400">{{ $t('Article approval will be enabled after content review is confirmed') }}</p>
                </div>
                <div v-else class="space-y-4">
                    <template v-for="(item, index) in autoContentItems" :key="item?.id || index">
                        <Card class="shadow-sm border border-surface-200">
                            <template #header>
                                <div class="p-4 pb-0 flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <span class="font-bold text-surface-900">{{ index + 1 }}</span>
                                        <div>
                                            <h4 class="font-semibold text-surface-900">{{ item.site_url || '-' }}</h4>
                                            <p class="text-sm text-surface-600">{{ item.type_name || '-' }}</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <Tag v-if="item.content.client_approved_at" :value="$t('Client Approved')" severity="success" icon="pi pi-check" />
                                        <Tag v-else-if="item.content.client_rejected_at" :value="$t('Client Rejected')" severity="danger" icon="pi pi-times" />
                                        <Tag v-else-if="item.content.sent_to_client_approval_at" :value="$t('Awaiting Client Approval')" severity="warn" icon="pi pi-clock" />
                                        <Tag v-else-if="item.content.content_link" :value="$t('Ready to Send')" severity="info" icon="pi pi-envelope" />
                                        <Tag v-else :value="$t('Link Pending')" severity="secondary" icon="pi pi-link" />
                                    </div>
                                </div>
                            </template>
                            <template #content>
                                <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <label class="block text-sm font-medium text-blue-900 mb-3">
                                        <i class="pi pi-file-word text-blue-600 mr-1"></i>
                                        {{ $t('Article Link (Google Docs)') }}
                                    </label>

                                    <!-- Link exists -->
                                    <div v-if="item.content.content_link" class="flex items-center justify-between gap-4">
                                        <div class="flex items-center gap-2 flex-1 min-w-0">
                                            <i class="pi pi-link text-primary-500 shrink-0"></i>
                                            <a :href="item.content.content_link" target="_blank"
                                               class="text-primary-500 hover:text-primary-600 underline break-all text-sm">
                                                {{ item.content.content_link }}
                                            </a>
                                        </div>
                                        <div class="flex gap-2 shrink-0">
                                            <Button
                                                :label="$t('Edit')"
                                                icon="pi pi-pencil"
                                                size="small"
                                                severity="secondary"
                                                @click="openAdminLinkModal(item.content.id)"
                                                :disabled="!!item.content.client_approved_at || (!!item.content.sent_to_client_approval_at && !item.content.client_rejected_at)"
                                            />
                                            <Button
                                                :label="$t('Clear')"
                                                icon="pi pi-trash"
                                                size="small"
                                                severity="danger"
                                                @click="clearContentLink(item.content.id)"
                                                :disabled="!!item.content.client_approved_at || (!!item.content.sent_to_client_approval_at && !item.content.client_rejected_at)"
                                            />
                                        </div>
                                    </div>

                                    <!-- No link yet -->
                                    <div v-else>
                                        <Button
                                            :label="$t('Add Google Docs Link')"
                                            icon="pi pi-plus"
                                            size="small"
                                            severity="info"
                                            @click="openAdminLinkModal(item.content.id)"
                                        />
                                    </div>

                                    <!-- Client approval status -->
                                    <div v-if="item.content.content_link" class="mt-3 space-y-2">
                                        <div v-if="item.content.client_approved_at" class="p-3 bg-green-50 rounded border border-green-200">
                                            <div class="flex items-center gap-2 text-green-700">
                                                <i class="pi pi-check-circle"></i>
                                                <span class="font-medium text-sm">{{ $t('Client approved the article') }}</span>
                                                <span class="text-xs text-green-500 ml-auto">{{ new Date(item.content.client_approved_at).toLocaleString() }}</span>
                                            </div>
                                        </div>
                                        <div v-else-if="item.content.client_rejected_at" class="p-3 bg-red-50 rounded border border-red-200">
                                            <div class="flex items-center gap-2 text-red-700 mb-1">
                                                <i class="pi pi-times-circle"></i>
                                                <span class="font-medium text-sm">{{ $t('Client rejected the article') }}</span>
                                                <span class="text-xs text-red-500 ml-auto">{{ new Date(item.content.client_rejected_at).toLocaleString() }}</span>
                                            </div>
                                            <p v-if="item.content.client_rejection_reason" class="text-sm text-red-800 mt-1">{{ item.content.client_rejection_reason }}</p>
                                        </div>
                                        <div v-else-if="item.content.sent_to_client_approval_at" class="p-3 bg-amber-50 rounded border border-amber-200">
                                            <div class="flex items-center gap-2 text-amber-700">
                                                <i class="pi pi-clock"></i>
                                                <span class="font-medium text-sm">{{ $t('Awaiting client approval') }}</span>
                                                <span class="text-xs text-amber-500 ml-auto">{{ $t('Sent') }}: {{ new Date(item.content.sent_to_client_approval_at).toLocaleString() }}</span>
                                            </div>
                                        </div>

                                        <!-- Send / Resend button -->
                                        <div v-if="!item.content.client_approved_at && (!item.content.sent_to_client_approval_at || item.content.client_rejected_at)"
                                             class="flex justify-end">
                                            <Button
                                                :label="item.content.client_rejected_at ? $t('Resend to Client') : $t('Send to Client for Approval')"
                                                :icon="item.content.client_rejected_at ? 'pi pi-refresh' : 'pi pi-send'"
                                                size="small"
                                                :severity="item.content.client_rejected_at ? 'warn' : 'info'"
                                                @click="openSendToClientApprovalModal(item.content.id)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </template>
                </div>
            </template>
        </Card>

        <!-- Add/Edit Link Modal -->
        <Dialog
            v-model:visible="showAdminLinkModal"
            :header="$t('Add Google Docs Link')"
            :style="{ width: '500px' }"
            :modal="true"
            :closable="!addingLink"
        >
            <InputText
                v-model="adminLinkInput[selectedContentId]"
                :placeholder="$t('https://docs.google.com/...')"
                class="w-full"
                :disabled="addingLink"
            />
            <template #footer>
                <div class="flex justify-end gap-3">
                    <Button :label="$t('Cancel')" severity="secondary" :disabled="addingLink" @click="closeAdminLinkModal" />
                    <Button :label="$t('Save')" icon="pi pi-check" severity="success" :loading="addingLink" @click="saveAdminLink" />
                </div>
            </template>
        </Dialog>

        <!-- Clear Link Confirmation -->
        <Dialog
            v-model:visible="showClearLinkModal"
            :header="$t('Clear Google Docs Link')"
            :style="{ width: '400px' }"
            :modal="true"
        >
            <p>{{ $t('Are you sure you want to clear the Google Docs link?') }}</p>
            <template #footer>
                <div class="flex justify-end gap-3">
                    <Button :label="$t('Cancel')" severity="secondary" @click="showClearLinkModal = false" />
                    <Button :label="$t('Clear')" icon="pi pi-trash" severity="danger" @click="confirmClearLink" />
                </div>
            </template>
        </Dialog>

        <!-- Send to Client Approval Modal -->
        <SendForApprovalModal
            :visible="showSendToClientApprovalModal"
            :loading="sendingToClientApproval"
            :client-locale="order.client?.locale || 'en'"
            @update:visible="(v) => { showSendToClientApprovalModal = v; if (!v) closeSendToClientApprovalModal(); }"
            @confirm="submitSendToClientApproval"
        />
    </div>
</template>
